import {
  CardRecord,
  MyIELTSDatabase,
  NewCardRecord,
  createCard,
  defaultDb,
  mergeCard,
  WritingCloze,
  SpeakingContent,
  ExtraMeta,
} from '../db/indexedDb';

const slugify = (value: string) =>
  value
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)/g, '')
    .slice(0, 48) || 'phrase';

export const buildSpeakingPhraseCardId = (sourceCard: CardRecord, phrase: string) =>
  `${sourceCard.contentId ?? sourceCard.id}-spkphrase-${slugify(phrase)}`;

const placeholderForIndex = (index: number) => `{{blank_${index}}}`;

function pickMissingSpan(better: string, transcript: string, minLen = 3, maxLen = 6): string | null {
  const tgt = better.toLowerCase().match(/[a-z']+/g) ?? [];
  const ref = new Set(transcript.toLowerCase().match(/[a-z']+/g) ?? []);
  for (let len = maxLen; len >= minLen; len -= 1) {
    for (let i = 0; i + len <= tgt.length; i += 1) {
      const span = tgt.slice(i, i + len);
      if (!span.length) continue;
      const allInRef = span.every((w) => ref.has(w));
      if (!allInRef) return span.join(' ');
    }
  }
  return null;
}

function buildCloze(templateSource: string, phrase: string): { template: string; answers: string[] } | null {
  if (!phrase.trim() || !templateSource.trim()) return null;
  const regex = new RegExp(phrase.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'i');
  if (!regex.test(templateSource)) return null;
  return {
    template: templateSource.replace(regex, placeholderForIndex(0)),
    answers: [phrase],
  };
}

export interface PhraseCardResult {
  created: boolean;
  updated: boolean;
  cardId?: string;
  reason?: string;
}

const dedupeExamples = (items: string[]) => {
  const seen = new Set<string>();
  return items
    .map((item) => item.trim())
    .filter(Boolean)
    .filter((item) => {
      const key = item.toLowerCase();
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
};

/**
 * Generate / upsert a speaking-derived phrase cloze card.
 * - Type: writing (so it can reuse cloze UI), tagged `speaking-phrase`.
 * - Dedup by id `${sourceCard.contentId}-spkphrase-${slug(phrase)}`.
 * - If phrase already present, only occurrence++ and refresh content.
 */
export async function generateSpeakingPhraseCard(
  sourceCard: CardRecord,
  transcript: string | undefined,
  betterVersion: string | undefined,
  db: MyIELTSDatabase = defaultDb
): Promise<PhraseCardResult> {
  const reference = (betterVersion ?? '').trim();
  if (!reference) return { created: false, updated: false, reason: 'no-better-version' };
  const span = pickMissingSpan(reference, transcript ?? '');
  if (!span) return { created: false, updated: false, reason: 'no-missing-span' };

  const cloze = buildCloze(reference, span);
  if (!cloze) return { created: false, updated: false, reason: 'cloze-not-built' };

  const id = buildSpeakingPhraseCardId(sourceCard, span);
  const now = Date.now();

  // Build a vocab-style card: en=phrase, cn=topic提示，examples含完整betterVersion句子
  const speakingContent = sourceCard.content as SpeakingContent | undefined;
  const cnHint =
    speakingContent?.topic ||
    speakingContent?.question ||
    sourceCard.speakingMeta?.topic ||
    '口语表达';
  const enPhrase = cloze.answers[0];
  const examples = [reference].filter(Boolean);

  const newCard: NewCardRecord = {
    id,
    type: 'vocab',
    mode: 'review',
    isRelearn: false,
    topics: sourceCard.topics ?? [],
    tags: [...(sourceCard.tags ?? []), 'speaking-phrase'],
    unitId: sourceCard.unitId,
    sourceId: sourceCard.id,
    contentId: id,
    contentVersion: `speaking-phrase-${sourceCard.contentVersion ?? 'v1'}`,
    nextReviewDate: now,
    content: {
      term: enPhrase,
      definition: cnHint,
      example: reference,
      examples,
      scene: '口语表达巩固',
      acceptableAnswers: cloze.answers,
      generatedAt: now,
    },
    vocabMeta: {
      en: enPhrase,
      cn: cnHint,
      examples,
      acceptableAnswers: cloze.answers,
      scene: 'speaking phrase',
      important: true,
      extraMeta: { sourceForCloze: sourceCard.id, from: 'speaking' },
    },
  };

  const existing = await db.cards.get(id);
  if (!existing) {
    await createCard(newCard, db);
    return { created: true, updated: false, cardId: id };
  }

  const existingExtra = existing.vocabMeta?.extraMeta;
  const occurrence = (existingExtra?.occurrence ?? 0) + 1;
  const merged = mergeCard(
    existing,
    {
      ...newCard,
      id,
      mode: existing.mode ?? 'review',
      history: existing.history,
      vocabMeta: {
        ...(existing.vocabMeta ?? {}),
        ...newCard.vocabMeta,
        extraMeta: { ...(existing.vocabMeta?.extraMeta ?? {}), occurrence } as ExtraMeta,
      },
      content: { ...(existing.content ?? {}), ...newCard.content, occurrence },
    },
    now,
    true
  );
  merged.vocabMeta = {
    ...(merged.vocabMeta ?? {}),
    ...newCard.vocabMeta,
    extraMeta: { ...(merged.vocabMeta?.extraMeta ?? {}), occurrence } as ExtraMeta,
  };
  merged.content = { ...(merged.content ?? {}), ...newCard.content, occurrence };
  merged.updatedAt = now;

  await db.cards.put(merged);
  return { created: false, updated: true, cardId: id };
}

/**
 * 手动添加口语短语到词汇卡（用于历史页点击添加）
 */
export async function createSpeakingPhraseVocabCard(
  sourceCard: CardRecord,
  phrase: string,
  exampleAnswer?: string,
  db: MyIELTSDatabase = defaultDb
): Promise<PhraseCardResult> {
  const trimmed = phrase.trim();
  if (!trimmed) return { created: false, updated: false, reason: 'empty-phrase' };

  const id = buildSpeakingPhraseCardId(sourceCard, trimmed);
  const now = Date.now();
  const speakingContent = sourceCard.content as SpeakingContent | undefined;
  const cnHint =
    speakingContent?.topic ||
    speakingContent?.question ||
    sourceCard.speakingMeta?.topic ||
    sourceCard.speakingMeta?.question ||
    '口语表达';
  const examples = dedupeExamples([exampleAnswer ?? '']);

  const newCard: NewCardRecord = {
    id,
    type: 'vocab',
    mode: 'review',
    isRelearn: false,
    topics: sourceCard.topics ?? [],
    tags: [...(sourceCard.tags ?? []), 'speaking-phrase'],
    unitId: sourceCard.unitId,
    sourceId: sourceCard.id,
    contentId: id,
    contentVersion: `speaking-phrase-${sourceCard.contentVersion ?? 'v1'}`,
    nextReviewDate: now,
    content: {
      term: trimmed,
      definition: cnHint,
      example: examples[0],
      examples,
      scene: '口语表达巩固',
      acceptableAnswers: [trimmed],
      generatedAt: now,
    },
    vocabMeta: {
      en: trimmed,
      cn: cnHint,
      examples,
      acceptableAnswers: [trimmed],
      scene: 'speaking phrase',
      important: true,
      extraMeta: { sourceForCloze: sourceCard.id, from: 'speaking', source: 'history-phrase' },
    },
  };

  const existing = await db.cards.get(id);
  if (!existing) {
    await createCard(newCard, db);
    return { created: true, updated: false, cardId: id };
  }

  const existingExtra = existing.vocabMeta?.extraMeta;
  const occurrence = (existingExtra?.occurrence ?? 0) + 1;
  const existingExamples = dedupeExamples([
    ...(existing.vocabMeta?.examples ?? []),
    ...(((existing.content ?? {}) as VocabContent).examples ?? []),
  ]);
  const mergedExamples = dedupeExamples([...existingExamples, ...examples]);

  const merged = mergeCard(
    existing,
    {
      ...newCard,
      id,
      mode: existing.mode ?? 'review',
      history: existing.history,
      vocabMeta: {
        ...(existing.vocabMeta ?? {}),
        ...newCard.vocabMeta,
        examples: mergedExamples,
        extraMeta: { ...(existing.vocabMeta?.extraMeta ?? {}), occurrence } as ExtraMeta,
      },
      content: {
        ...(existing.content ?? {}),
        ...newCard.content,
        examples: mergedExamples,
        occurrence,
      },
    },
    now,
    true
  );
  merged.vocabMeta = {
    ...(merged.vocabMeta ?? {}),
    ...newCard.vocabMeta,
    examples: mergedExamples,
    extraMeta: { ...(merged.vocabMeta?.extraMeta ?? {}), occurrence } as ExtraMeta,
  };
  merged.content = { ...(merged.content ?? {}), ...newCard.content, examples: mergedExamples, occurrence };
  merged.updatedAt = now;

  await db.cards.put(merged);
  return { created: false, updated: true, cardId: id };
}
